<template>
    <v-container class="donation-page" :class="{ 'pa-4': $vuetify.breakpoint.smAndDown }">
        <!-- Loading State -->
        <v-row v-if="isLoading" class="justify-center mt-16">
            <v-col cols="12" class="text-center">
                <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                <p class="mt-4">Loading campaign...</p>
            </v-col>
        </v-row>

        <!-- Campaign Not Found -->
        <v-row v-else-if="!campaign" class="justify-center mt-16">
            <v-col cols="12" md="8" lg="6" class="text-center">
                <v-icon size="80" color="grey lighten-1">mdi-alert-circle-outline</v-icon>
                <h2 class="mt-4">Campaign Not Found</h2>
                <p class="mt-2 grey--text">
                    The campaign you're looking for doesn't exist or may have been removed.
                </p>
                <v-btn color="primary" depressed @click="$router.push('/')" class="mt-4">
                    Go to Homepage
                </v-btn>
            </v-col>
        </v-row>

        <!-- Campaign Content -->
        <v-card v-else elevation="0" class="campaign-card mx-auto" max-width="800">
            <!-- Student Header -->
            <div class="campaign-header pa-6 pa-md-8">
                <v-row align="center">
                    <v-col cols="12" md="auto" class="text-center text-md-left">
                        <v-avatar size="100" color="#F4F5F6">
                            <v-img :src="campaign.user.image_url">
                                <template v-slot:placeholder>
                                    <v-icon color="grey lighten-1" size="50">mdi-account-outline</v-icon>
                                </template>
                            </v-img>
                        </v-avatar>
                    </v-col>
                    <v-col cols="12" md class="text-center text-md-left">
                        <h1 class="campaign-student-name">{{ campaign.user.name }}</h1>
                        <p class="campaign-tagline mt-2">Help me reach my goal!</p>
                    </v-col>
                </v-row>
            </div>

            <!-- Student Message -->
            <div v-if="campaign.message" class="campaign-message-section px-6 px-md-8 py-4">
                <v-card elevation="0" color="#f8f9fa" class="pa-4 campaign-message-card">
                    <p class="campaign-message">"{{ campaign.message }}"</p>
                </v-card>
            </div>

            <!-- Campaign Ended Banner -->
            <v-alert
                v-if="isCampaignEnded"
                type="info"
                class="mx-4 mx-md-8 mt-4"
                outlined
                dense
            >
                This campaign ended on {{ formatDate(campaign.updated_at) }}
            </v-alert>

            <!-- Progress Section -->
            <div class="px-6 px-md-8 py-4">
                <CampaignProgress
                    :donations="campaign.donations"
                    :goal="campaign.amount"
                />
            </div>

            <!-- Itemized Progress Section -->
            <div v-if="isItemizedCampaign" class="px-6 px-md-8 py-4">
                <CampaignItemsProgress
                    :items="campaign.items"
                />
            </div>

            <!-- Donation Amount Selection -->
            <div class="px-6 px-md-8 py-4">
                <DonationAmountPicker
                    ref="amountPicker"
                    :disabled="isCampaignEnded"
                    :loading="isCreatingIntent && !isItemizedCampaign"
                    :button-text="isItemizedCampaign ? 'Continue with' : 'Donate'"
                    @submit="onDonationAmountSelected"
                />
            </div>

            <!-- Allocation Picker for Itemized Campaigns -->
            <div v-if="isItemizedCampaign && donationAmount > 0" class="px-6 px-md-8 py-4">
                <DonationAllocationPicker
                    ref="allocationPicker"
                    :items="campaign.items"
                    :donation-amount="donationAmount"
                    @allocationsChanged="onAllocationsChanged"
                />
            </div>

            <!-- Proceed to Payment Button (only for itemized campaigns) -->
            <div v-if="isItemizedCampaign && donationAmount > 0" class="px-6 px-md-8 py-4">
                <v-btn
                    color="primary"
                    depressed
                    block
                    height="52"
                    class="proceed-btn"
                    :loading="isCreatingIntent"
                    @click="proceedToPayment"
                >
                    <v-icon left>mdi-heart</v-icon>
                    Proceed to Donate ${{ donationAmount }}
                </v-btn>
            </div>

            <!-- Share Section -->
            <div class="px-6 px-md-8 py-4">
                <CampaignShare
                    :share-url="campaignUrl"
                    :user-name="campaign.user.name"
                />
            </div>

            <!-- Recent Donations -->
            <div class="px-6 px-md-8 py-4 pb-8">
                <CampaignDonations
                    :donations="campaign.donations"
                />
            </div>
        </v-card>

        <!-- Payment Modal -->
        <v-dialog v-model="showPaymentModal" max-width="730" persistent>
            <DonationPaymentModal
                v-if="showPaymentModal"
                :campaign="campaign"
                :amount="donationAmount"
                :client-secret="clientSecret"
                :allocations="donationAllocations"
                @close="closePaymentModal"
                @paymentCompleted="onPaymentCompleted"
            />
        </v-dialog>

        <!-- Thank You Dialog -->
        <v-dialog v-model="showThankYouDialog" max-width="500">
            <v-card class="thank-you-card pa-6 text-center">
                <v-btn icon absolute right top @click="showThankYouDialog = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>

                <v-icon color="success" size="64" class="mb-4">mdi-check-circle</v-icon>
                <h2 class="mb-3">Thank You!</h2>
                <p class="mb-4">
                    Your donation of <strong>${{ lastDonationAmount }}</strong> has been processed successfully.
                    You're helping {{ campaign?.user?.name }} reach their goal!
                </p>

                <v-card elevation="0" color="#f4f5f6" class="pa-4 mb-4">
                    <p class="mb-3">Share this campaign to help even more:</p>
                    <div class="d-flex justify-center">
                        <!-- SMS -->
                        <a :href="thankYouSmsShareUrl" class="share-link mr-3">
                            <v-avatar size="40" color="#25D366" class="share-icon">
                                <v-icon color="white" size="22">mdi-message-text</v-icon>
                            </v-avatar>
                        </a>
                        <!-- Facebook -->
                        <a
                            :href="thankYouFacebookShareUrl"
                            class="share-link mr-3"
                            @click.prevent="openShareWindow(thankYouFacebookShareUrl)"
                        >
                            <v-img
                                height="40"
                                width="40"
                                contain
                                src="@/assets/icons/campaign-facebook.png"
                                class="share-icon"
                            ></v-img>
                        </a>
                        <!-- X (Twitter) -->
                        <a
                            :href="thankYouTwitterShareUrl"
                            class="share-link mr-3"
                            @click.prevent="openShareWindow(thankYouTwitterShareUrl)"
                        >
                            <v-avatar size="40" color="#000" class="share-icon">
                                <span class="x-logo">X</span>
                            </v-avatar>
                        </a>
                        <!-- Snapchat -->
                        <a
                            href="#"
                            class="share-link"
                            v-clipboard="campaignUrl"
                            @click.prevent="onSnapchatShare"
                        >
                            <v-img
                                height="40"
                                width="40"
                                contain
                                src="@/assets/icons/campaign-snapchat.svg"
                                class="share-icon rounded-circle"
                            ></v-img>
                        </a>
                    </div>
                </v-card>

                <v-btn color="primary" depressed @click="showThankYouDialog = false">
                    Close
                </v-btn>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import moment from 'moment'
import CampaignService from '@/services/campaign.service'
import CampaignProgress from '@/components/campaign/CampaignProgress.vue'
import CampaignItemsProgress from '@/components/campaign/CampaignItemsProgress.vue'
import CampaignShare from '@/components/campaign/CampaignShare.vue'
import CampaignDonations from '@/components/campaign/CampaignDonations.vue'
import DonationAmountPicker from '@/components/campaign/DonationAmountPicker.vue'
import DonationAllocationPicker from '@/components/campaign/DonationAllocationPicker.vue'
import DonationPaymentModal from '@/components/campaign/DonationPaymentModal.vue'
import { calculateDestinationFee } from '@/utils/helpers'

export default {
    name: 'Donation',
    components: {
        CampaignProgress,
        CampaignItemsProgress,
        CampaignShare,
        CampaignDonations,
        DonationAmountPicker,
        DonationAllocationPicker,
        DonationPaymentModal
    },
    metaInfo() {
        return {
            title: this.metaTitle,
            meta: [
                { name: 'description', content: this.metaDescription },
                { property: 'og:title', content: this.metaTitle },
                { property: 'og:description', content: this.metaDescription },
                { property: 'og:url', content: this.campaignUrl },
                { property: 'og:type', content: 'website' },
                { name: 'twitter:card', content: 'summary' },
                { name: 'twitter:title', content: this.metaTitle },
                { name: 'twitter:description', content: this.metaDescription }
            ]
        }
    },
    data: () => ({
        campaign: null,
        isLoading: true,
        isCreatingIntent: false,
        showPaymentModal: false,
        showThankYouDialog: false,
        clientSecret: null,
        donationAmount: 0,
        lastDonationAmount: 0,
        donationAllocations: []
    }),
    props: {
        hash: {
            type: String,
            required: false
        }
    },
    computed: {
        campaignHash() {
            return this.$route.params.hash || this.$route.query.cmp
        },
        metaTitle() {
            if (!this.campaign?.user?.name) return 'Support a Student - EDUrent'
            return `Help ${this.campaign.user.name} - EDUrent`
        },
        metaDescription() {
            if (!this.campaign?.user?.name) return 'Support a student\'s education goals through EDUrent.'
            return `Help ${this.campaign.user.name} reach their fundraising goal of $${this.campaign.amount}. Every donation makes a difference!`
        },
        campaignUrl() {
            return `${process.env.VUE_APP_CLIENT_BASE_URL}/campaign/${this.campaignHash}`
        },
        isCampaignEnded() {
            if (!this.campaign) return false
            return this.campaign.is_active !== 1 || this.campaign.is_ended === 1
        },
        isItemizedCampaign() {
            return this.campaign?.is_itemized && this.campaign?.items?.length > 0
        },
        thankYouSmsMessage() {
            return `I just donated to help ${this.campaign?.user?.name} raise funds for college! You can help too: ${this.campaignUrl}`
        },
        thankYouShareTitle() {
            return `I just donated to help ${this.campaign?.user?.name}! You can too.`
        },
        thankYouFacebookShareUrl() {
            const url = encodeURIComponent(this.campaignUrl)
            const quote = encodeURIComponent(this.thankYouShareTitle)
            return `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`
        },
        thankYouTwitterShareUrl() {
            const url = encodeURIComponent(this.campaignUrl)
            const text = encodeURIComponent(this.thankYouShareTitle)
            return `https://x.com/intent/tweet?url=${url}&text=${text}&hashtags=edurent`
        },
        thankYouSmsShareUrl() {
            const body = encodeURIComponent(this.thankYouSmsMessage)
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
            return isIOS ? `sms:&body=${body}` : `sms:?body=${body}`
        }
    },
    methods: {
        async loadCampaign() {
            if (!this.campaignHash) {
                this.$store.commit('setSnackbar', {
                    show: true,
                    message: 'The campaign you are looking for does not exist.',
                    color: 'error'
                })
                this.$router.push('/')
                return
            }

            this.isLoading = true
            try {
                this.campaign = await CampaignService.getCampaignByHash(this.campaignHash)
            } catch (error) {
                this.campaign = null
            } finally {
                this.isLoading = false
            }
        },
        onDonationAmountSelected(amount) {
            this.donationAmount = amount
            // For non-itemized campaigns, proceed directly to payment
            if (!this.isItemizedCampaign) {
                this.proceedToPayment()
            }
        },
        onAllocationsChanged(allocations) {
            this.donationAllocations = allocations
        },
        async proceedToPayment() {
            if (this.isCampaignEnded || !this.donationAmount) return

            // Validate allocations if itemized
            if (this.isItemizedCampaign && this.$refs.allocationPicker) {
                if (!this.$refs.allocationPicker.isValid()) {
                    this.$store.commit('setSnackbar', {
                        show: true,
                        message: 'Total allocation cannot exceed your donation amount.',
                        color: 'error'
                    })
                    return
                }
            }

            this.isCreatingIntent = true

            try {
                const paymentFee = calculateDestinationFee(this.donationAmount)
                const response = await CampaignService.createPaymentIntent(this.campaignHash, {
                    amount: this.donationAmount,
                    paymentFee: paymentFee.toFixed(2)
                })
                this.clientSecret = response.clientSecret
                this.showPaymentModal = true
            } catch (error) {
                const message = error?.response?.data?.message || 'Unable to process donation. Please try again.'
                this.$store.commit('setSnackbar', {
                    show: true,
                    message,
                    color: 'error'
                })
            } finally {
                this.isCreatingIntent = false
            }
        },
        async initiateDonation(amount) {
            if (this.isCampaignEnded) return

            this.donationAmount = amount
            this.isCreatingIntent = true

            try {
                const paymentFee = calculateDestinationFee(amount)
                const response = await CampaignService.createPaymentIntent(this.campaignHash, {
                    amount: amount,
                    paymentFee: paymentFee.toFixed(2)
                })
                this.clientSecret = response.clientSecret
                this.showPaymentModal = true
            } catch (error) {
                const message = error?.response?.data?.message || 'Unable to process donation. Please try again.'
                this.$store.commit('setSnackbar', {
                    show: true,
                    message,
                    color: 'error'
                })
            } finally {
                this.isCreatingIntent = false
            }
        },
        closePaymentModal() {
            this.showPaymentModal = false
            this.clientSecret = null
        },
        async onPaymentCompleted() {
            this.lastDonationAmount = this.donationAmount.toFixed(2)
            this.closePaymentModal()
            this.showThankYouDialog = true

            // Reset picker via ref if needed
            this.donationAmount = 0
            this.donationAllocations = []

            // Refresh campaign data
            await this.loadCampaign()
        },
        formatDate(date) {
            return moment(date).format('LL')
        },
        onSnapchatShare() {
            this.$store.commit('setSnackbar', {
                show: true,
                message: 'Link copied! Share it on your Snapchat Story.',
                color: 'success'
            })
        },
        openShareWindow(url) {
            const width = 600
            const height = 400
            const left = (window.innerWidth - width) / 2
            const top = (window.innerHeight - height) / 2
            window.open(
                url,
                'share',
                `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`
            )
        }
    },
    created() {
        this.loadCampaign()
    }
}
</script>

<style scoped>
.donation-page {
    max-width: 900px;
    padding-top: 24px;
    padding-bottom: 48px;
}

.campaign-card {
    border-radius: 12px;
    overflow: hidden;
}

.campaign-header {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.campaign-student-name {
    font-size: 28px;
    font-weight: 700;
    color: #333;
    line-height: 1.3;
}

.campaign-tagline {
    font-size: 18px;
    color: #666;
    margin: 0;
}

.campaign-message-card {
    border-radius: 10px !important;
}

.campaign-message {
    font-size: 16px;
    font-style: italic;
    color: #444;
    line-height: 1.6;
    margin: 0;
    white-space: pre-wrap;
}

.share-link {
    text-decoration: none;
    display: inline-block;
}

.share-icon {
    cursor: pointer;
    transition: transform 0.2s ease;
}

.share-icon:hover {
    transform: scale(1.1);
}

.thank-you-card {
    border-radius: 12px !important;
}

.thank-you-card h2 {
    font-size: 28px;
    font-weight: 700;
    color: #333;
}

.x-logo {
    color: white;
    font-size: 20px;
    font-weight: 700;
    font-family: Arial, sans-serif;
}

.proceed-btn {
    font-size: 16px !important;
    font-weight: 600 !important;
    border-radius: 10px !important;
    text-transform: none !important;
}

@media (max-width: 600px) {
    .campaign-student-name {
        font-size: 22px;
    }

    .campaign-tagline {
        font-size: 16px;
    }
}
</style>
